import { googleAnalyticsService } from '@web/services/singletons';

/**
 * The value {@link useGoogleAnalytics} hands back. Built once at module scope rather than rebuilt on every call so
 * that the returned object keeps a stable identity across renders and is safe to place in a dependency array.
 */
const GOOGLE_ANALYTICS_HOOK_VALUE = {
    /**
     * The web Google Analytics 4 service singleton. Call `track(GoogleAnalyticsEvents.X, properties)` on it, always
     * with a literal event name so the property types are checked at the call site.
     */
    googleAnalyticsService,
} as const;

/**
 * Gives a component access to the Google Analytics service.
 *
 * Deliberately thin today: it returns the service singleton and nothing else, which is no more than importing the
 * singleton directly would give you. It exists so that every call site standardizes on one access point now, and any
 * later addition (swapping in a no-op service behind a feature flag, sourcing ambient params from React context,
 * per-component instrumentation) can be made here instead of by editing every call site in the app a second time.
 *
 * @returns an object holding the Google Analytics service singleton
 *
 * @example
 * const { googleAnalyticsService } = useGoogleAnalytics();
 * googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetOpen, { open_mode: 'edit' });
 */
export function useGoogleAnalytics() {
    return GOOGLE_ANALYTICS_HOOK_VALUE;
}
